import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Pagination,
  Card,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Avatar,
  Rating,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  Legend
} from 'recharts';
import {
  People as AvailableIcon,
  GroupWork as UnderUtilizedIcon,
  HourglassEmpty as BenchIcon,
  TrendingDown as ShortageIcon,
  TrendingUp as DemandIcon,
  Science as SimulatorIcon,
  Science as ScienceIcon,
  SwapHoriz as ReassignIcon,
  School as ReskillIcon,
  PersonAdd as HireIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DONUT_COLORS = ['#22c55e', '#eab308', '#ef4444', '#94a3b8', '#3b82f6'];

const ResourcePlanningDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [error, setError] = useState('');
  const [timeframeDays, setTimeframeDays] = useState(60);

  // Filters for Capacity & Workload
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [benchOnly, setBenchOnly] = useState(false);

  // Search & Pagination for Workforce
  const [searchTerm, setSearchTerm] = useState('');
  const [staffPage, setStaffPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  // Deep-Dive Modal State
  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [deepDiveData, setDeepDiveData] = useState(null);

  // What-If Simulator Sandbox State
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [scenarioType, setScenarioType] = useState('PROJECT_DELAY');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [delayDays, setDelayDays] = useState(14);
  const [newProjectName, setNewProjectName] = useState('New Project Proposal');
  const [newProjectSkill, setNewProjectSkill] = useState('Python');
  const [newProjectQty, setNewProjectQty] = useState(2);
  const [simulating, setSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  // Skill Matrix Dialog State
  const [openSkillDialog, setOpenSkillDialog] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [projectSkills, setProjectSkills] = useState([
    { skill_id: 1, skillName: 'Java Core & Spring Boot', category: 'Backend', requiredProficiency: 4, priority: 'Critical', experienceYears: 4, domain: 'Banking', quantity: 3 },
    { skill_id: 2, skillName: 'React & TypeScript', category: 'Frontend', requiredProficiency: 4, priority: 'High', experienceYears: 3, domain: 'Fintech', quantity: 2 },
    { skill_id: 3, skillName: 'Docker & Kubernetes', category: 'DevOps', requiredProficiency: 5, priority: 'Critical', experienceYears: 5, domain: 'Cloud', quantity: 2 }
  ]);
  const [newSkill, setNewSkill] = useState({
    skillName: '',
    category: 'Backend',
    requiredProficiency: 3,
    priority: 'Medium',
    experienceYears: 2,
    domain: '',
    quantity: 1
  });



  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      let token = localStorage.getItem('token') || localStorage.getItem('accessToken') || '';
      if (token.startsWith('"') && token.endsWith('"')) {
        token = token.slice(1, -1);
      }

      let company_id = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        company_id = payload.company_id;
      } catch (e) {}

      const promises = [
        fetch(`${API_BASE_URL}/api/resource-planning/dashboard-summary?days=${timeframeDays}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/resource-planning/recommendations?days=${timeframeDays}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ];

      if (company_id) {
        promises.push(
          fetch(`${API_BASE_URL}/api/cmp/${company_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        );
      }

      const results = await Promise.all(promises);
      const dashRes = results[0];
      const recRes = results[1];
      let cmpRes = null;
      
      if (company_id) {
        cmpRes = results[2];
      }

      const dashResult = await dashRes.json();
      const recResult = await recRes.json();

      if (cmpRes && cmpRes.ok) {
        try {
          const cmpResult = await cmpRes.json();
          if (cmpResult?.data) {
            setCompanyConfig(cmpResult.data);
          }
        } catch(e) {}
      }

      if (dashResult.success) {
        setData(dashResult);
        if (dashResult.projectCosts && dashResult.projectCosts.length > 0 && !selectedProjectId) {
          setSelectedProjectId(dashResult.projectCosts[0].projectId);
        }
        if (dashResult.shortageForecast && dashResult.shortageForecast.length > 0) {
          const dynamicSkills = dashResult.shortageForecast.map((sf, idx) => ({
            skill_id: sf.requirementId || idx + 1,
            skillName: sf.skillName,
            category: sf.category || (idx % 2 === 0 ? 'Backend' : 'DevOps'),
            requiredProficiency: sf.requiredProficiency || 4,
            priority: sf.requiredHeadcount > 3 ? 'Critical' : sf.requiredHeadcount > 1 ? 'High' : 'Medium',
            experienceYears: sf.experienceYears || 3,
            domain: sf.domain || 'Engineering',
            quantity: sf.requiredHeadcount || 1
          }));
          setProjectSkills(dynamicSkills);
        }
      } else {
        setError(dashResult.message || 'Failed to load Resource Planning Dashboard data');
      }

      if (recResult.success) {
        setRecommendations(recResult.data);
      }
    } catch (err) {
      //console.error('Error fetching dashboard summary:', err);
      setError('Network error connecting to PulseHR Resource Planning server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [timeframeDays, selectedMonth, selectedYear, selectedDepartment, benchOnly]);

  const handleOpenDeepDive = async (cardTypeOrObj) => {
    setDeepDiveOpen(true);
    
    // If passed a custom object (e.g. employee detail, match breakdown, project cost detail)
    if (typeof cardTypeOrObj === 'object' && cardTypeOrObj !== null) {
      setDeepDiveLoading(false);
      setDeepDiveData(cardTypeOrObj);
      return;
    }

    const cardType = cardTypeOrObj;
    setDeepDiveLoading(true);
    setDeepDiveData(null);

    const defaultMeta = {
      total: {
        what: "Overview of all active technical workforce employees in the company.",
        why: "Total count of technical employees across all allocation statuses (Bench, Under-utilized, Healthy, Fully Utilized, Overloaded)."
      },
      available: {
        what: "Overview of all unassigned or highly available employees in the technical workforce.",
        why: "Free Capacity â‰¥ 50%. These resources are either 100% on bench or have significant free capacity available for immediate project assignment."
      },
      idle: {
        what: "Under-utilized resources with partial project assignment.",
        why: "Free Capacity is between 1% and 49% (Allocation < 80%). They are partially assigned to active projects but have remaining bandwidth that can be utilized."
      },
      overloaded: {
        what: "Employees assigned to over 100% of standard work capacity.",
        why: "Total Allocation > 100% across multiple active projects. Indicates high risk of burnout, delivery delays, and quality degradation."
      },
      skill_shortages: {
        what: "Required skill headcounts that are missing or unfilled across active projects.",
        why: "Skill Shortage = Required Headcount - Assigned Headcount. Calculated by comparing project skill requirements against active assigned team members."
      },
      upcoming_rolloffs: {
        what: "Resources whose project assignments are ending in the next 30 days.",
        why: "Project End Date is within 30 days. These employees will soon complete their current tasks and become available for reassignment."
      },
      healthy: {
        what: "Employees with optimal workload utilization (50%â€“85%).",
        why: "Allocation % is between 50% and 85%. Represents balanced capacity ensuring steady productivity without causing overload."
      },
      fully_utilized: {
        what: "Employees fully assigned at peak work capacity (86%â€“100%).",
        why: "Allocation % is between 86% and 100%. Indicates these employees are 100% committed to project deliverables with 0% free capacity."
      }
    };

    try {
      let token = localStorage.getItem('token') || localStorage.getItem('accessToken') || '';
      if (token.startsWith('"') && token.endsWith('"')) token = token.slice(1, -1);

      const res = await fetch(`${API_BASE_URL}/api/resource-planning/card-deep-dive/${cardType}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const result = await res.json();
      if (result.success) {
        const meta = defaultMeta[cardType] || {};
        setDeepDiveData({
          ...result.data,
          what: meta.what || result.data.explanation,
          why: meta.why || `Calculated based on active project allocations for category: ${cardType}`
        });
      } else {
        const meta = defaultMeta[cardType] || {};
        let fallbackItems = [];
        if (cardType === 'overloaded') {
          fallbackItems = workforce.filter(w => w.freeCapacityPercentage < 0 || w.utilizationStatus === 'Overloaded');
        } else if (cardType === 'idle') {
          fallbackItems = workforce.filter(w => w.freeCapacityPercentage > 0 && w.freeCapacityPercentage < 50);
        } else if (cardType === 'available') {
          fallbackItems = workforce.filter(w => w.freeCapacityPercentage >= 50);
        } else if (cardType === 'total') {
          fallbackItems = workforce;
        }
        setDeepDiveData({
          cardType,
          title: `${cardType.toUpperCase()} Detail View`,
          explanation: result.message || 'Displaying dynamic dashboard breakdown',
          what: meta.what,
          why: meta.why,
          items: fallbackItems
        });
      }
    } catch (err) {
      //console.error('Error fetching card deep dive:', err);
    } finally {
      setDeepDiveLoading(false);
    }
  };

  const handleRunSimulation = async (type = scenarioType, delay = delayDays) => {
    setSimulating(true);
    setSimulationResult(null);
    try {
      let token = localStorage.getItem('token') || localStorage.getItem('accessToken') || '';
      if (token.startsWith('"') && token.endsWith('"')) token = token.slice(1, -1);

      const bodyPayload = type === 'PROJECT_DELAY'
        ? { scenarioType: type, projectId: selectedProjectId, delayDays: parseInt(delay, 10) }
        : {
            scenarioType: type,
            newProjectName,
            newProjectRequiredSkills: [{ skillName: newProjectSkill, quantity: parseInt(newProjectQty, 10) }]
          };

      const res = await fetch(`${API_BASE_URL}/api/resource-planning/simulate-scenario`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyPayload)
      });

      const result = await res.json();
      if (result.success) {
        setSimulationResult(result);
        if (!simulatorOpen) setSimulatorOpen(true);
      }
    } catch (err) {
      //console.error('Simulation error:', err);
    } finally {
      setSimulating(false);
    }
  };



  const handleSaveSkill = () => {
    if (!newSkill.skillName.trim()) return;

    if (editingSkill) {
      setProjectSkills(prev => prev.map(s => s.skill_id === editingSkill.skill_id ? { ...s, ...newSkill } : s));
    } else {
      const created = { skill_id: Date.now(), ...newSkill };
      setProjectSkills(prev => [...prev, created]);
    }

    setOpenSkillDialog(false);
    setEditingSkill(null);
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="85vh" gap={2}>
        <CircularProgress size={50} sx={{ color: '#0284c7' }} />
        <Typography variant="h6" fontWeight="600" color="#334155">Loading PulseHR Resource Planning Hub...</Typography>
      </Box>
    );
  }

  const kpis = data?.executiveKpis || {};
  const workload = data?.workloadDistribution || {};
  const forecast = data?.shortageForecast || [];
  const projectCosts = data?.projectCosts || [];
  const workforce = data?.workforceDetails || [];
  const userRoleDisplay = data?.userRole || 'CPO / Leadership';

  const donutData = [
    { name: 'Healthy (50-85%)', value: workload.healthyCount || 0 },
    { name: 'Fully Utilized (86-100%)', value: workload.highUtilizationCount || 0 },
    { name: 'Under-utilized (<50%)', value: workload.underutilizedCount || 0 },
    { name: 'Bench (0%)', value: workload.benchCount || 0 }
  ].filter(item => item.value > 0);

  const totalEmployees = kpis.totalTechnicalWorkforce || 0;

  const filteredWorkforce = workforce
    .filter(w => benchOnly ? w.freeCapacityPercentage === 100 : w.freeCapacityPercentage > 0)
    .filter(w => {
      if (!selectedDepartment) return true;
      return (w.department || '').toLowerCase() === selectedDepartment.toLowerCase();
    })
    .filter(w => {
      const q = searchTerm.toLowerCase().trim();
      if (!q) return true;
      return (
        (w.employeeName || '').toLowerCase().includes(q) ||
        (w.designation || '').toLowerCase().includes(q) ||
        (w.department || '').toLowerCase().includes(q) ||
        (w.utilizationStatus || '').toLowerCase().includes(q) ||
        (w.employee_id || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => b.freeCapacityPercentage - a.freeCapacityPercentage);

  const totalPages = Math.ceil(filteredWorkforce.length / ITEMS_PER_PAGE) || 1;
  const paginatedStaff = filteredWorkforce.slice((staffPage - 1) * ITEMS_PER_PAGE, staffPage * ITEMS_PER_PAGE);

  const matchData = forecast.flatMap(f => (f.topResourceMatches || []).map(m => {
    const rawVal = m.rawMatchScore ?? m.matchScore ?? 0;
    const numScore = typeof rawVal === 'number' ? rawVal : parseInt(String(rawVal).replace('%', ''), 10) || 0;
    const firstName = m.employeeName ? m.employeeName.split(' ')[0] : 'Candidate';
    return {
      skill: f.skillName,
      candidate: m.employeeName,
      label: `${f.skillName} (${firstName})`,
      matchScore: numScore,
      displayScore: `${numScore}%`
    };
  })).slice(0, 5);

  const forecastChartData = forecast
    .map(f => {
      const demand = f.requiredHeadcount || 0;
      const available = f.availableSupply || 0;
      const shortage = Math.max(0, demand - available);
      return {
        skill: f.skillName,
        demand,
        available,
        shortage,
        projectDetails: f.projectDetails || []
      };
    })
    .filter(f => f.shortage > 0);

  const hasAnyShortage = forecastChartData.length > 0;

  return (
    <Box p={3} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' }}>
      
      {/* ðŸš€ TOP HEADER BAR WITH DYNAMIC ROLE SCOPE BADGE & AI CONTROLS */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="800" color="#0f172a" letterSpacing="-0.5px">
            Resource Planning Dashboard
          </Typography>
          <Typography variant="body2" color="#64748b" fontWeight="500">
            Workforce Capacity, Skill Matching, Demand Forecasting & Role-Scoped Analytics
          </Typography>
        </Box>

        {/* <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            variant="contained"
            onClick={() => setSimulatorOpen(true)}
            startIcon={<SimulatorIcon />}
            sx={{
              borderRadius: 2,
              px: 2.5,
              py: 1,
              fontWeight: 700,
              fontSize: '0.85rem',
              textTransform: 'none',
              bgcolor: '#0284c7',
              boxShadow: '0 4px 12px rgba(2,132,199,0.2)',
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Run What-If Simulator
          </Button>
        </Box> */}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

       {/* ðŸ“Š ROW 1: TOP 5 INTERACTIVE KPI CARDS */}
      <Grid container spacing={2} mb={3}>
        
        {/* Metric 1: Total Employees */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            onClick={() => handleOpenDeepDive('total')}
            sx={{
              p: 2,
              borderRadius: 3,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 20px -4px rgba(0,0,0,0.08)', borderColor: '#0284c7' }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" fontWeight="700" color="#64748b">Total Employees</Typography>
              <Box p={0.8} borderRadius={2} bgcolor="#e0f2fe">
                <AvailableIcon sx={{ fontSize: 18, color: '#0284c7' }} />
              </Box>
            </Box>
            <Typography variant="h4" fontWeight="800" color="#0f172a" my={0.5}>
              {totalEmployees || workforce.length || 0}
            </Typography>
            <Box display="inline-flex" alignItems="center" px={1} py={0.2} borderRadius={1.5} bgcolor="#e0f2fe">
              <Typography variant="caption" fontWeight="700" color="#0369a1" sx={{ fontSize: '0.65rem' }}>
                Technical Workforce
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Metric 2: Under-utilized */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            onClick={() => handleOpenDeepDive('idle')}
            sx={{
              p: 2,
              borderRadius: 3,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 20px -4px rgba(0,0,0,0.08)', borderColor: '#d97706' }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" fontWeight="700" color="#64748b">Under-utilized</Typography>
              <Box p={0.8} borderRadius={2} bgcolor="#fffbeb">
                <UnderUtilizedIcon sx={{ fontSize: 18, color: '#d97706' }} />
              </Box>
            </Box>
            <Typography variant="h4" fontWeight="800" color="#d97706" my={0.5}>
              {workload.underutilizedCount || 0}
            </Typography>
            <Box display="inline-flex" alignItems="center" px={1} py={0.2} borderRadius={1.5} bgcolor="#fffbeb">
              <Typography variant="caption" fontWeight="700" color="#b45309" sx={{ fontSize: '0.65rem' }}>
                Free Cap. 1-49%
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Metric 3: Bench Employees */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            onClick={() => handleOpenDeepDive('available')}
            sx={{
              p: 2,
              borderRadius: 3,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 20px -4px rgba(0,0,0,0.08)', borderColor: '#475569' }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" fontWeight="700" color="#64748b">Bench Employees</Typography>
              <Box p={0.8} borderRadius={2} bgcolor="#f1f5f9">
                <BenchIcon sx={{ fontSize: 18, color: '#475569' }} />
              </Box>
            </Box>
            <Typography variant="h4" fontWeight="800" color="#1e293b" my={0.5}>
              {workload.benchCount || 0}
            </Typography>
            <Box display="inline-flex" alignItems="center" px={1} py={0.2} borderRadius={1.5} bgcolor="#f1f5f9">
              <Typography variant="caption" fontWeight="700" color="#475569" sx={{ fontSize: '0.65rem' }}>
                100% Unassigned
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Metric 4: Upcoming Shortages */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            onClick={() => handleOpenDeepDive({
              title: 'Project Skill Shortage & Unfilled Positions Breakdown',
              cardType: 'skill_shortages',
              what: 'Detailed breakdown of missing employee headcount per project skill requirement. Compares required project skills and headcount specs against available internal employees.',
              why: 'Shortage = Required Project Headcount - Available Internal Employees with Free Capacity â‰¥ 50%. A critical shortage occurs when active project requirements exceed available qualified staff.',
              items: forecast
                .map(f => {
                  const req = f.requiredHeadcount || 1;
                  const avail = f.availableSupply || 0;
                  const missing = Math.max(0, req - avail);
                  return {
                    requirementId: f.skill_id,
                    projectName: (f.projectDetails && f.projectDetails.length > 0)
                      ? f.projectDetails.map(pd => pd.projectName).join(', ')
                      : 'Active Project Pipeline',
                    skillName: f.skillName,
                    category: f.category || 'Engineering',
                    requiredProficiency: f.requiredProficiency || 4,
                    requiredHeadcount: req,
                    assignedHeadcount: avail,
                    missingHeadcount: missing,
                    startDate: f.projectDetails?.[0]?.startDate
                  };
                })
                .filter(item => item.missingHeadcount > 0)
            })}
            sx={{
              p: 2,
              borderRadius: 3,
              height: '95%',
              background: '#ffffff',
              border: '1px solid #fed7aa',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 20px -4px rgba(234,88,12,0.12)', borderColor: '#ea580c' }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="caption" fontWeight="700" color="#9a3412">Skill Shortages</Typography>
              <Tooltip title="Missing employee headcount across active projects where required headcount exceeds available staff">
                <Box p={0.8} borderRadius={2} bgcolor="#fff7ed">
                  <ShortageIcon sx={{ fontSize: 18, color: '#ea580c' }} />
                </Box>
              </Tooltip>
            </Box>
            <Typography variant="h4" fontWeight="800" color="#ea580c" my={0.3}>
              {forecast.filter(f => (f.requiredHeadcount - f.availableSupply) > 0).length || kpis.activeCriticalShortagesCount || 0}
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.3} mt={0.5}>
              <Box display="inline-flex" alignItems="center" px={0.8} py={0.2} borderRadius={1.5} bgcolor="#fff7ed">
                <Typography variant="caption" fontWeight="700" color="#c2410c" sx={{ fontSize: '0.62rem' }}>
                  Unfilled Roles (Missing Staff)
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Metric 5: Upcoming Project Needs */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            onClick={() => handleOpenDeepDive({
              title: 'Upcoming Project Skill & Role Requirements',
              cardType: 'future_demand',
              what: 'Total employee headcount required across upcoming project skill requirements over the next 60 days.',
              why: 'Sum of all required employee headcount specs configured for upcoming active projects.',
              items: forecast.map(f => ({
                requirementId: f.skill_id,
                projectName: (f.projectDetails && f.projectDetails.length > 0)
                  ? f.projectDetails.map(pd => pd.projectName).join(', ')
                  : 'Active Project Pipeline',
                skillName: f.skillName,
                category: f.category || 'Engineering',
                requiredProficiency: f.requiredProficiency || 4,
                requiredHeadcount: f.requiredHeadcount || 1,
                startDate: f.projectDetails?.[0]?.startDate
              }))
            })}
            sx={{
              p: 2,
              borderRadius: 3,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 20px -4px rgba(0,0,0,0.08)', borderColor: '#0284c7' }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" fontWeight="700" color="#64748b">Upcoming Project Needs</Typography>
              <Box p={0.8} borderRadius={2} bgcolor="#f0f9ff">
                <DemandIcon sx={{ fontSize: 18, color: '#0284c7' }} />
              </Box>
            </Box>
            <Typography variant="h4" fontWeight="800" color="#0f172a" my={0.5}>
              {forecast.reduce((acc, curr) => acc + (curr.requiredHeadcount || 0), 0)}
            </Typography>
            <Box display="inline-flex" alignItems="center" px={1} py={0.2} borderRadius={1.5} bgcolor="#f0f9ff">
              <Typography variant="caption" fontWeight="700" color="#0369a1" sx={{ fontSize: '0.65rem' }}>
                Employees Required
              </Typography>
            </Box>
          </Paper>
        </Grid>

      </Grid>



      {/* ðŸš€ ROW 2: MAIN DASHBOARD PANELS (PANEL 1 DONUT CHART, PANEL 2 CAPACITY TABLE, PANEL 3 MATCH ENGINE GRAPH) */}
      <Grid container spacing={2.5} mb={3}>
        
        {/* PANEL 1: Utilization Overview (Donut Chart) */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, background: '#ffffff', border: '1px solid #e2e8f0', minHeight: 380, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight="700" color="#0f172a" mb={1}>
              Utilization Overview
            </Typography>

            <Box position="relative" height={190} my={1}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={4} dataKey="value" style={{ cursor: 'pointer' }}>
                    {donutData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={DONUT_COLORS[index % DONUT_COLORS.length]}
                        onClick={() => {
                          const type = entry.name.includes('Healthy') ? 'healthy' : entry.name.includes('Fully Utilized') ? 'fully_utilized' : entry.name.includes('Under-utilized') ? 'idle' : entry.name.includes('Overloaded') ? 'overloaded' : 'available';
                          handleOpenDeepDive(type);
                        }}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>

              <Box position="absolute" top="50%" left="50%" sx={{ transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <Typography variant="caption" color="#64748b" fontWeight="600" display="block">Total Employees</Typography>
                <Typography variant="h5" fontWeight="800" color="#0f172a">{totalEmployees}</Typography>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={0.8}>
              <Box display="flex" justifyContent="space-between" onClick={() => handleOpenDeepDive('healthy')} sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f1f5f9' } }}>
                <Typography variant="caption" color="#475569" fontWeight="600">ðŸŸ¢ Healthy (50-85%)</Typography>
                <Typography variant="caption" fontWeight="700">{workload.healthyCount || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" onClick={() => handleOpenDeepDive('fully_utilized')} sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f1f5f9' } }}>
                <Typography variant="caption" color="#475569" fontWeight="600">ðŸ”µ Fully Utilized (86-100%)</Typography>
                <Typography variant="caption" fontWeight="700" color="#2563eb">{workload.highUtilizationCount || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" onClick={() => handleOpenDeepDive('idle')} sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f1f5f9' } }}>
                <Typography variant="caption" color="#475569" fontWeight="600">ðŸŸ¡ Under-utilized (&lt;50%)</Typography>
                <Typography variant="caption" fontWeight="700">{workload.underutilizedCount || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" onClick={() => handleOpenDeepDive('available')} sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f1f5f9' } }}>
                <Typography variant="caption" color="#475569" fontWeight="600">âšª Bench (0%)</Typography>
                <Typography variant="caption" fontWeight="700">{workload.benchCount || 0}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* PANEL 2: Capacity & Availability (Who is Available?) */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, background: '#ffffff', border: '1px solid #e2e8f0', minHeight: 380, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                Capacity & Availability
              </Typography>

              <TextField
                size="small"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setStaffPage(1); }}
                sx={{ width: 100 }}
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b' }}>Employee</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b' }}>Role / Skill</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b' }}>Free Capacity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStaff.map((emp, idx) => (
                    <TableRow
                      key={idx}
                      onClick={() => handleOpenDeepDive({
                        title: `Employee Profile & Capacity Breakdown: ${emp.employeeName}`,
                        cardType: 'employee_detail',
                        what: `Detailed capacity, project allocation history, and skills profile for ${emp.employeeName}.`,
                        why: `Free Capacity is ${emp.freeCapacityPercentage}%. Computed as (100% standard capacity - total assigned project allocation percentage of ${100 - emp.freeCapacityPercentage}%).`,
                        employee: emp
                      })}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                    >
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: '700', color: '#0f172a' }}>{emp.employeeName}</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: '#64748b' }}>{emp.designation}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" fontWeight="700" color={emp.freeCapacityPercentage >= 50 ? '#166534' : '#dc2626'}>{emp.freeCapacityPercentage}%</Typography>
                          <LinearProgress variant="determinate" value={emp.freeCapacityPercentage} sx={{ width: 40, height: 5, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: emp.freeCapacityPercentage >= 50 ? '#22c55e' : '#ef4444' } }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={1}>
                <Pagination count={totalPages} page={staffPage} onChange={(e, p) => setStaffPage(p)} size="small" />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* PANEL 3: Resource Match Engine Graph */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, background: '#ffffff', border: '1px solid #e2e8f0', minHeight: 380, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                Resource Match Engine
              </Typography>
              <Chip label="Candidate Match" size="small" sx={{ fontSize: '0.68rem', fontWeight: '700', height: 20, bgcolor: '#e0f2fe', color: '#0284c7', border: '1px solid #bae6fd' }} />
            </Box>

            {matchData.length > 0 ? (
              <Box height={280} mt={1}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={matchData}
                    margin={{ top: 5, right: 35, left: 5, bottom: 5 }}
                    style={{ cursor: 'pointer' }}
                    onClick={(state) => {
                      if (state && state.activePayload && state.activePayload.length > 0) {
                        const entry = state.activePayload[0].payload;
                        handleOpenDeepDive({
                          title: `Resource Match Breakdown: ${entry.candidate} for ${entry.skill}`,
                          cardType: 'match_detail',
                          what: `Candidate Match Evaluation for ${entry.candidate} against requirement (${entry.skill}).`,
                          why: `Match Score of ${entry.displayScore} is calculated by comparing candidate skill expertise, experience depth, domain alignment, and availability against requirement criteria.`,
                          candidate: entry.candidate,
                          skill: entry.skill,
                          matchScore: entry.displayScore,
                          rawScore: entry.matchScore
                        });
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} unit="%" />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: '#334155', fontWeight: 600 }} width={115} />
                    <RechartsTooltip
                      formatter={(value, name, props) => [`${value}% Match`, `Candidate: ${props.payload.candidate}`]}
                      labelFormatter={(label) => `Requirement: ${label}`}
                      contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                    />
                    <Bar dataKey="matchScore" radius={[0, 6, 6, 0]} barSize={14}>
                      {matchData.map((entry, index) => (
                        <Cell
                          key={`match-bar-${index}`}
                          fill={entry.matchScore >= 80 ? '#10b981' : entry.matchScore >= 60 ? '#0284c7' : '#f59e0b'}
                        />
                      ))}
                      <LabelList dataKey="displayScore" position="right" style={{ fontSize: '10px', fontWeight: '700', fill: '#334155' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={270}>
                <Typography variant="caption" color="#64748b">No match data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

      </Grid>

      {/* ðŸš€ ROW 3: SHORTAGE & DEMAND FORECAST & AI EXECUTIVE BRIEFING (PANEL 4 & PANEL 5) */}
      <Grid container spacing={2.5} mb={3}>
        
        {/* PANEL 4: Project Shortage & Demand Forecast */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, background: '#ffffff', border: '1px solid #e2e8f0', minHeight: 450, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                Shortage & Demand Forecast
              </Typography>
              <Chip
                label="Time-Series Forecast"
                size="small"
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: '700',
                  height: 20,
                  bgcolor: '#f0f9ff',
                  color: '#0369a1',
                  border: '1px solid #bae6fd'
                }}
              />
            </Box>

            {forecastChartData.length > 0 ? (
              <Box height={360} width="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={forecastChartData}
                    margin={{ top: 15, right: 20, left: 0, bottom: 20 }}
                    style={{ cursor: 'pointer' }}
                    onClick={(state) => {
                      if (state && state.activePayload && state.activePayload.length > 0) {
                        const clickedData = state.activePayload[0].payload;
                        handleOpenDeepDive({
                          title: `Demand & Shortage Breakdown for Skill: ${clickedData.skill}`,
                          cardType: 'graph_skill_forecast',
                          what: `Skill demand forecasting and shortage overview for ${clickedData.skill}.`,
                          why: `Demand = ${clickedData.demand} headcount, Available = ${clickedData.available} supply, Shortage = ${clickedData.shortage} missing positions. Shortage occurs when project demand exceeds available workforce with matching skills.`,
                          items: clickedData.projectDetails || [],
                          skill: clickedData.skill
                        });
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="skill" tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }} dy={5} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                    <RechartsTooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const items = payload.filter(p => p.dataKey !== 'shortage' || (typeof p.value === 'number' && p.value > 0));
                        return (
                          <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}>
                            <Typography variant="caption" fontWeight="700" color="#334155" display="block" mb={0.5}>
                              Skill: {label}
                            </Typography>
                            {items.map((entry, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                fontWeight="700"
                                display="block"
                                sx={{ color: entry.dataKey === 'demand' ? '#2563eb' : entry.dataKey === 'available' ? '#16a34a' : '#dc2626' }}
                              >
                                {entry.dataKey === 'demand' ? 'Demand' : entry.dataKey === 'available' ? 'Available' : 'Shortage'} : {entry.value}
                              </Typography>
                            ))}
                          </Paper>
                        );
                      }}
                    />
                    <Legend
                      verticalAlign="top"
                      height={36}
                      formatter={(value) => (
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                          {value === 'demand' ? 'Demand' : value === 'available' ? 'Available' : 'Shortage'}
                        </span>
                      )}
                    />
                    <Bar dataKey="demand" fill="#3b82f6" name="demand" radius={[4, 4, 0, 0]} barSize={16}>
                      <LabelList dataKey="demand" position="top" style={{ fontSize: '10px', fontWeight: '700', fill: '#1d4ed8' }} />
                    </Bar>
                    <Bar dataKey="available" fill="#22c55e" name="available" radius={[4, 4, 0, 0]} barSize={16}>
                      <LabelList dataKey="available" position="top" style={{ fontSize: '10px', fontWeight: '700', fill: '#15803d' }} />
                    </Bar>
                    {hasAnyShortage && (
                      <Bar dataKey="shortage" fill="#ef4444" name="shortage" radius={[4, 4, 0, 0]} barSize={16}>
                        {forecastChartData.map((entry, index) => (
                          <Cell key={`shortage-cell-${index}`} fill={entry.shortage > 0 ? '#ef4444' : 'transparent'} stroke={entry.shortage > 0 ? '#ef4444' : 'transparent'} />
                        ))}
                        <LabelList
                          dataKey="shortage"
                          position="top"
                          formatter={(val) => (val > 0 ? val : '')}
                          style={{ fontSize: '10px', fontWeight: '700', fill: '#b91c1c' }}
                        />
                      </Bar>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={360} gap={1}>
                <Typography variant="body2" fontWeight="700" color="#16a34a">
                  ðŸŽ‰ Zero Skill Shortages Detected
                </Typography>
                <Typography variant="caption" color="#64748b" textAlign="center">
                  All active project requirements have 100% available employee supply.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* PANEL 5: Reassignment & Reskilling Recommendations */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, background: '#ffffff', border: '1px solid #e2e8f0', minHeight: 450, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                  Reassignment & Reskilling Recommendations
                </Typography>
                <Chip label="Strategic Action Engine" size="small" sx={{ fontSize: '0.68rem', fontWeight: '700', height: 20, bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }} />
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: 350, overflowY: 'auto' }}>
                {recommendations && recommendations.length > 0 ? (
                  recommendations.map((rec, idx) => {
                    const opt1 = rec.recommendations?.option1_Reassign;
                    const opt2 = rec.recommendations?.option2_Reskill;
                    const opt3 = rec.recommendations?.option3_Hire;
                    const opt1Candidates = opt1?.candidates || [];
                    const opt2Candidates = opt2?.candidates || [];
                    const opt3Action = opt3?.recommendedAction;

                    return (
                      <Box
                        key={idx}
                        p={1.8}
                        borderRadius={2.5}
                        onClick={() => handleOpenDeepDive({
                          title: `Strategic Staffing Recommendation: ${rec.skillName}`,
                          cardType: 'recommendation_detail',
                          what: `Reassignment & Skill Gap Resolution Strategy for ${rec.skillName} shortage (${rec.requiredHeadcount} headcount needed).`,
                          why: `PulseHR evaluated candidate availability, skill overlap, and external recruitment effort to recommend the optimal staffing strategy (${opt1Candidates.length > 0 ? 'Option 1: Internal Reassignment' : opt2Candidates.length > 0 ? 'Option 2: Reskilling' : 'Option 3: External Hiring'}).`,
                          recommendation: rec
                        })}
                        sx={{ border: '1px solid #e2e8f0', bg: '#f8fafc', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: '#0284c7', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' } }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2" fontWeight="700" color="#0f172a">
                            Shortage: {rec.skillName} ({rec.requiredHeadcount} Devs needed)
                          </Typography>
                          <Chip label={`Category: ${rec.category || 'Tech'}`} size="small" sx={{ fontSize: '0.68rem', fontWeight: '700', height: 20 }} />
                        </Box>

                        {opt1?.details ? (
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <ReassignIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                            <Typography variant="caption" fontWeight="600" color="#15803d">
                              ðŸ”„ <strong>Reassign:</strong> {opt1.details}
                            </Typography>
                          </Box>
                        ) : opt2?.details ? (
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <ReskillIcon sx={{ fontSize: 16, color: '#0284c7' }} />
                            <Typography variant="caption" fontWeight="600" color="#0369a1">
                              ðŸŽ“ <strong>Reskill & Train:</strong> {opt2.details}
                            </Typography>
                          </Box>
                        ) : opt1Candidates.length > 0 ? (
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <ReassignIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                            <Typography variant="caption" fontWeight="600" color="#15803d">
                              ðŸ”„ <strong>Reassign:</strong> Move <strong>{opt1Candidates[0].name}</strong> to project
                            </Typography>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <HireIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                            <Typography variant="caption" fontWeight="600" color="#b91c1c">
                              ðŸ“¢ <strong>External Hire:</strong> {opt3?.details || opt3Action || `Initiate recruitment for ${rec.skillName}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="caption" color="#64748b">All project requirements are fully staffed!</Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

      </Grid>

      {/* ðŸš€ INTERACTIVE CARD CLICK DEEP-DIVE DIALOG */}
      <Dialog open={deepDiveOpen} onClose={() => setDeepDiveOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: '800', color: '#0f172a', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          ðŸ” {deepDiveData?.title || 'Interactive Detail Deep Dive'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {deepDiveLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={2}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="#64748b">Fetching detailed dynamic breakdown...</Typography>
            </Box>
          ) : (
            <>
              {/* ðŸ’¡ WHAT & WHY EXPLANATION BANNER */}
              {deepDiveData && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    border: '1px solid #bae6fd'
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                    <Box p={0.8} borderRadius={2} bgcolor="#0284c7" color="#fff" display="flex">
                      <ScienceIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="800" color="#0369a1" letterSpacing="-0.2px">
                        ðŸ“Œ What is this?
                      </Typography>
                      <Typography variant="body2" color="#0f172a" fontWeight="500" mt={0.3} sx={{ lineHeight: 1.5 }}>
                        {deepDiveData.what || deepDiveData.explanation || 'Comprehensive detail view and breakdown of selected resource metric.'}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5, borderColor: '#bae6fd' }} />

                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    <Box p={0.8} borderRadius={2} bgcolor="#0369a1" color="#fff" display="flex">
                      <ScienceIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="800" color="#0c4a6e" letterSpacing="-0.2px">
                        ðŸ’¡ Why did this value / calculation come?
                      </Typography>
                      <Typography variant="body2" color="#334155" fontWeight="500" mt={0.3} sx={{ lineHeight: 1.5 }}>
                        {deepDiveData.why || 'Calculated in real-time based on active project allocations, headcount demand, and workforce capacity records.'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* CASE 1: EMPLOYEE DETAIL DEEP DIVE */}
              {deepDiveData?.cardType === 'employee_detail' && deepDiveData.employee ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#ffffff', borderColor: '#e2e8f0' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box>
                        <Typography variant="h6" fontWeight="800" color="#0f172a">
                          {deepDiveData.employee.employeeName}
                        </Typography>
                        <Typography variant="caption" color="#64748b" fontWeight="500">
                          Role: {deepDiveData.employee.designation} | Dept: {deepDiveData.employee.department || 'Engineering'} | Location: {deepDiveData.employee.location || 'Bangalore'}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${deepDiveData.employee.freeCapacityPercentage}% Free Capacity`}
                        color={deepDiveData.employee.freeCapacityPercentage >= 50 ? 'success' : 'warning'}
                        sx={{ fontWeight: '800' }}
                      />
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" fontWeight="700" color="#334155" mb={1}>
                      Active Project Allocations (Total: {100 - deepDiveData.employee.freeCapacityPercentage}% Allocated)
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: '700' }}>Project Name</TableCell>
                          <TableCell sx={{ fontWeight: '700' }}>Role</TableCell>
                          <TableCell sx={{ fontWeight: '700' }}>Allocation %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(deepDiveData.employee.projects || []).length > 0 ? (
                          deepDiveData.employee.projects.map((p, pIdx) => (
                            <TableRow key={pIdx}>
                              <TableCell sx={{ fontWeight: '700' }}>{p.projectName}</TableCell>
                              <TableCell>{p.role || 'Contributor'}</TableCell>
                              <TableCell sx={{ fontWeight: '700', color: '#0284c7' }}>{p.allocationPct}%</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              No active project assignments. Resource is 100% on Bench.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              ) : deepDiveData?.cardType === 'match_detail' ? (
                /* CASE 2: MATCH ENGINE SCORE DEEP DIVE */
                <Box display="flex" flexDirection="column" gap={2}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#ffffff', borderColor: '#e2e8f0' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="h6" fontWeight="800" color="#0f172a">
                            Candidate: {deepDiveData.candidate}
                          </Typography>
                          <Chip label="Match Engine" size="small" sx={{ fontSize: '0.65rem', fontWeight: '700', bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }} />
                        </Box>
                        <Typography variant="body2" color="#64748b" fontWeight="600">
                          Requirement Target Skill: {deepDiveData.skill}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color="#64748b" fontWeight="600" display="block">Match Score</Typography>
                        <Typography variant="h4" fontWeight="800" color={deepDiveData.rawScore >= 80 ? '#10b981' : '#0284c7'}>
                          {deepDiveData.matchScore}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="subtitle2" fontWeight="700" color="#334155" mb={1.5}>
                      Multi-Factor Algorithm Evaluation Breakdown:
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f8fafc' }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block">Skill Overlap (40%)</Typography>
                          <Typography variant="subtitle1" fontWeight="800" color="#10b981">
                            {deepDiveData.rawScore >= 80 ? '95%' : '80%'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f8fafc' }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block">Proficiency Depth (30%)</Typography>
                          <Typography variant="subtitle1" fontWeight="800" color="#0284c7">
                            Level 4 / 5
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f8fafc' }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block">Domain Alignment (15%)</Typography>
                          <Typography variant="subtitle1" fontWeight="800" color="#8b5cf6">
                            High
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f8fafc' }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block">Availability (15%)</Typography>
                          <Typography variant="subtitle1" fontWeight="800" color="#059669">
                            Immediate
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              ) : deepDiveData?.cardType === 'project_cost_detail' && deepDiveData.project ? (
                /* CASE 3: PROJECT COST DEEP DIVE */
                <Box display="flex" flexDirection="column" gap={2}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#ffffff', borderColor: '#e2e8f0' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight="800" color="#0f172a">
                          {deepDiveData.project.projectName}
                        </Typography>
                        <Typography variant="body2" color="#64748b">
                          Assigned Team Members: {deepDiveData.project.teamMembersCount || 'Active Team'}
                        </Typography>
                      </Box>
                      <Chip
                        label={deepDiveData.project.isProfitable ? 'âœ… Within Budget' : 'âš ï¸ Over Budget'}
                        color={deepDiveData.project.isProfitable ? 'success' : 'error'}
                        sx={{ fontWeight: '800' }}
                      />
                    </Box>

                    <Grid container spacing= {2} mb={2}>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block">Monthly Project Budget</Typography>
                          <Typography variant="h5" fontWeight="800" color="#0f172a">
                            {companyConfig?.currency_symbol || 'â‚¹'}{(deepDiveData.project.projectMonthlyBudget || 0).toLocaleString(undefined)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: deepDiveData.project.isProfitable ? '#f0fdf4' : '#fef2f2' }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block">Current Monthly Resource Cost</Typography>
                          <Typography variant="h5" fontWeight="800" color={deepDiveData.project.isProfitable ? '#16a34a' : '#dc2626'}>
                            {companyConfig?.currency_symbol || 'â‚¹'}{deepDiveData.project.monthlyResourceCost.toLocaleString(undefined)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              ) : deepDiveData?.cardType === 'recommendation_detail' && deepDiveData.recommendation ? (
                /* CASE 4: RECOMMENDATION DEEP DIVE */
                <Box display="flex" flexDirection="column" gap={2}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, bgcolor: '#ffffff', borderColor: '#e2e8f0' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6" fontWeight="800" color="#0f172a">
                        Requirement: {deepDiveData.recommendation.skillName}
                      </Typography>
                      <Chip label={`${deepDiveData.recommendation.requiredHeadcount} Devs Required`} color="primary" sx={{ fontWeight: '800' }} />
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="subtitle2" fontWeight="700" color="#334155" mb={1}>
                      Available Strategic Resolution Options:
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {(() => {
                        const rec = deepDiveData.recommendation;
                        const opt1 = rec.recommendations?.option1_Reassign;
                        const opt2 = rec.recommendations?.option2_Reskill;
                        const opt3 = rec.recommendations?.option3_Hire;

                        const opt1Candidates = opt1?.candidates || [];
                        const opt2Candidates = opt2?.candidates || [];

                        return (
                          <>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
                              <Typography variant="subtitle2" fontWeight="800" color="#166534">
                                Option 1: Internal Bench Reassignment ({opt1?.status || (opt1Candidates.length > 0 ? 'Recommended' : 'No Immediate Match')})
                              </Typography>
                              <Typography variant="body2" color="#15803d" mt={0.5}>
                                {opt1?.details || (opt1Candidates.length > 0
                                  ? `Reassign available bench staff: ${opt1Candidates.map(c => `${c.name} (${c.freeCapacity || 100}% Free)`).join(', ')}. Immediate project onboarding.`
                                  : `No 100% matching bench staff available for immediate transfer.`)}
                              </Typography>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f9ff', borderColor: '#bae6fd' }}>
                              <Typography variant="subtitle2" fontWeight="800" color="#0369a1">
                                Option 2: Internal Skill Upskilling / Reskilling ({opt2?.status || 'Feasible'})
                              </Typography>
                              <Typography variant="body2" color="#0284c7" mt={0.5}>
                                {opt2?.details || (opt2Candidates.length > 0
                                  ? `Enroll available developers (${opt2Candidates.map(c => `${c.name}`).join(', ')}) into a 2-week fast-track ${rec.skillName} training program.`
                                  : `Cross-train available technical staff into a 2-week fast-track ${rec.skillName} training program.`)}
                              </Typography>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff7ed', borderColor: '#fed7aa' }}>
                              <Typography variant="subtitle2" fontWeight="800" color="#c2410c">
                                Option 3: External Candidate Hiring ({opt3?.status || 'Fallback'})
                              </Typography>
                              <Typography variant="body2" color="#ea580c" mt={0.5}>
                                {opt3?.details || opt3?.recommendedAction || `Initiate external recruitment drive for ${rec.skillName} (${rec.requiredHeadcount} headcount needed). Estimated lead time: 30 days.`}
                              </Typography>
                            </Paper>
                          </>
                        );
                      })()}
                    </Box>
                  </Paper>
                </Box>
              ) : deepDiveData?.cardType === 'graph_skill_forecast' ? (
                /* CASE 5: GRAPH FORECAST SKILL DEEP DIVE */
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                        <TableCell sx={{ fontWeight: '700' }}>Project Name</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Quantity Needed</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Start Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deepDiveData.items?.length > 0 ? (
                        deepDiveData.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: '700' }}>{item.projectName}</TableCell>
                            <TableCell sx={{ fontWeight: '700' }}>{item.quantityNeeded}</TableCell>
                            <TableCell>
                              {item.startDate ? new Date(item.startDate).toLocaleDateString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No active project requirements mapped to this skill.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : deepDiveData?.cardType === 'skill_shortages' ? (
                /* CASE 6: SKILL SHORTAGES DEEP DIVE */
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                        <TableCell sx={{ fontWeight: '700' }}>Target Project(s)</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Required Skill & Category</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Level (1-5)</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Headcount Needed</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Available Employees</TableCell>
                        <TableCell sx={{ fontWeight: '700', color: '#dc2626' }}>Missing Shortage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deepDiveData.items?.length > 0 ? (
                        deepDiveData.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: '700', color: '#0f172a' }}>{item.projectName}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="700" color="#0369a1">{item.skillName}</Typography>
                                <Chip label={item.category || 'Tech'} size="small" sx={{ fontSize: '0.65rem', height: 18, mt: 0.3 }} />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={`Level ${item.requiredProficiency || 4}`} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ fontWeight: '700' }}>{item.requiredHeadcount}</TableCell>
                            <TableCell sx={{ fontWeight: '700', color: '#16a34a' }}>{item.assignedHeadcount}</TableCell>
                            <TableCell>
                              {item.missingHeadcount > 0 ? (
                                <Chip label={`ðŸ”´ ${item.missingHeadcount} Missing`} color="error" size="small" sx={{ fontWeight: '800' }} />
                              ) : (
                                <Chip label="ðŸŸ¢ 0 Missing (Sufficient)" color="success" size="small" sx={{ fontWeight: '800' }} />
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={6} align="center">No active skill shortages found!</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : deepDiveData?.cardType === 'future_demand' ? (
                /* CASE 7: FUTURE DEMAND DEEP DIVE */
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f9ff' }}>
                        <TableCell sx={{ fontWeight: '700' }}>Target Project(s)</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Required Skill & Category</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Proficiency Level</TableCell>
                        <TableCell sx={{ fontWeight: '700', color: '#0284c7' }}>Employees Required</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deepDiveData.items?.length > 0 ? (
                        deepDiveData.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: '700', color: '#0f172a' }}>{item.projectName}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="700" color="#0369a1">{item.skillName}</Typography>
                                <Chip label={item.category || 'Tech'} size="small" sx={{ fontSize: '0.65rem', height: 18, mt: 0.3 }} />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={`Level ${item.requiredProficiency || 4}`} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ fontWeight: '800', color: '#0284c7' }}>
                              {item.requiredHeadcount} Employees Needed
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={4} align="center">No upcoming project skill requirements found!</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : deepDiveData?.cardType === 'overloaded' ? (
                /* CASE 7: OVERLOADED DEEP DIVE */
                <Box display="flex" flexDirection="column" gap={2}>
                  {deepDiveData.items?.length > 0 ? (
                    deepDiveData.items.map((emp, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#fecaca', bgcolor: '#fff5f5' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="800" color="#991b1b">{emp.employeeName} ({emp.employeeId})</Typography>
                            <Typography variant="caption" color="#64748b">{emp.designation} | {emp.department} | {emp.location}</Typography>
                          </Box>
                          <Chip label={`ðŸ”´ Total Allocation: ${emp.totalAllocationPct}%`} color="error" sx={{ fontWeight: '800' }} />
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" fontWeight="700" color="#334155" mb={0.5} display="block">Active Project Breakdown:</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontSize: '0.7rem', fontWeight: '700' }}>Project Name</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', fontWeight: '700' }}>Assigned Role</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', fontWeight: '700' }}>Allocation %</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(emp.projects || []).map((p, pIdx) => (
                              <TableRow key={pIdx}>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: '700' }}>{p.projectName}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{p.role}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', fontWeight: '700', color: '#dc2626' }}>{p.allocationPct}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    ))
                  ) : (
                    <Typography align="center" color="#64748b">No overloaded employees found!</Typography>
                  )}
                </Box>
              ) : (
                /* CASE 8: GENERAL LIST DEEP DIVE (IDLE, BENCH, HEALTHY, ROLL-OFFS) */
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                        <TableCell sx={{ fontWeight: '700' }}>Employee Name</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Designation</TableCell>
                        <TableCell sx={{ fontWeight: '700' }}>Location</TableCell>
                        {deepDiveData?.cardType !== 'total' && (
                          <TableCell sx={{ fontWeight: '700' }}>Allocation Details</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deepDiveData?.items?.length > 0 ? (
                        deepDiveData.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: '700' }}>{item.employeeName || item.employeeId}</TableCell>
                            <TableCell>{item.designation || 'Staff'}</TableCell>
                            <TableCell>{item.location || 'Bangalore'}</TableCell>
                            {deepDiveData?.cardType !== 'total' && (
                              <TableCell>
                                {deepDiveData.cardType === 'idle' ? (
                                  <Chip label={`Free Cap: ${item.freeCapacityPct}% (Used: ${item.totalAllocationPct}%)`} color="warning" size="small" sx={{ fontWeight: '700' }} />
                                ) : deepDiveData.cardType === 'available' ? (
                                  <Chip label="100% Free / Unassigned" color="success" size="small" sx={{ fontWeight: '700' }} />
                                ) : deepDiveData.cardType === 'healthy' ? (
                                  <Chip label={`Healthy Allocation (${item.totalAllocationPct || 70}%)`} color="success" size="small" sx={{ fontWeight: '700' }} />
                                ) : (
                                  <Chip label={`Roll-off Date: ${new Date(item.endDate || Date.now()).toLocaleDateString()}`} color="primary" size="small" sx={{ fontWeight: '700' }} />
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={deepDiveData?.cardType === 'total' ? 3 : 4} align="center">No items found for this detail view.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button variant="contained" onClick={() => setDeepDiveOpen(false)} sx={{ fontWeight: '700' }}>
            Close Explanation
          </Button>
        </DialogActions>
      </Dialog>

      {/* WHAT-IF SIMULATOR MODAL DIALOG */}
      <Dialog open={simulatorOpen} onClose={() => setSimulatorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: '800', color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>ðŸ§ª What-If Scenario Planning Sandbox</span>
          <Chip label="Scenario Engine" size="small" sx={{ fontSize: '0.65rem', fontWeight: '700', height: 20, bgcolor: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' }} />
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Test hypothetical schedule delays or new deal proposals in-memory. Zero database changes will occur.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Simulation Scenario Type</InputLabel>
            <Select value={scenarioType} label="Simulation Scenario Type" onChange={(e) => setScenarioType(e.target.value)}>
              <MenuItem value="PROJECT_DELAY">Project Schedule Delay (e.g. 14 Days)</MenuItem>
              <MenuItem value="SIMULATE_NEW_PROJECT">New Client Deal Proposal (Simulate Demand)</MenuItem>
            </Select>
          </FormControl>

          {scenarioType === 'PROJECT_DELAY' ? (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Select Target Project to Delay</InputLabel>
                <Select
                  value={selectedProjectId}
                  label="Select Target Project to Delay"
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                >
                  {projectCosts.map(p => (
                    <MenuItem key={p.projectId} value={p.projectId}>
                      {p.projectName} ({p.teamMembersCount} Team Members)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                type="number"
                label="Delay Duration (Days)"
                value={delayDays}
                onChange={(e) => setDelayDays(e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          ) : (
            <>
              <TextField fullWidth size="small" label="New Project Name" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={8}><TextField fullWidth size="small" label="Required Skill Name" value={newProjectSkill} onChange={(e) => setNewProjectSkill(e.target.value)} /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Qty" value={newProjectQty} onChange={(e) => setNewProjectQty(e.target.value)} /></Grid>
              </Grid>
            </>
          )}

          {simulationResult && (
            <Box mt={3} p={2} borderRadius={2.5} sx={{ background: simulationResult.verdict?.status === 'SAFE_DECISION' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${simulationResult.verdict?.status === 'SAFE_DECISION' ? '#bbf7d0' : '#fecaca'}` }}>
              <Typography variant="subtitle2" fontWeight="700" color={simulationResult.verdict?.status === 'SAFE_DECISION' ? '#166534' : '#991b1b'} mb={0.5}>
                Simulation Outcome: {simulationResult.verdict?.status}
              </Typography>
              <Typography variant="caption" fontWeight="600" color="#334155" display="block" mb={1}>
                ðŸ“Œ <strong>Scenario:</strong> {simulationResult.scenario?.description}
              </Typography>
              <Typography variant="body2" fontWeight="500" color={simulationResult.verdict?.status === 'SAFE_DECISION' ? '#15803d' : '#dc2626'}>
                {simulationResult.verdict?.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bg: '#f8fafc' }}>
          <Button variant="outlined" onClick={() => setSimulatorOpen(false)} sx={{ fontWeight: '700' }}>
            Close Sandbox
          </Button>
          <Button variant="contained" onClick={() => handleRunSimulation()} disabled={simulating} sx={{ fontWeight: '700', bg: '#0284c7' }}>
            {simulating ? 'Simulating...' : 'Run In-Memory Simulation'}
          </Button>
        </DialogActions>
      </Dialog>



    </Box>
  );
};

export default ResourcePlanningDashboard;
